import styled from '@emotion/styled';
import type { ReactElement } from 'react';
import { memo, useEffect, useState } from 'react';

import type { CountryCode } from '@hotjar/country-lib';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { Button, tokens } from '@hotjar/hotsauce';
import { CreditCardDetailsLoader } from './CreditCardDetailsLoader';
import type { CallbackResponse, CallbackFailure } from './hooks/useZuoraLibrary';
import { useZuoraLibrary } from './hooks/useZuoraLibrary';
import { useZuoraParameters } from './hooks/useZuoraParameters';
import type { CallbackState } from './utils/emitCallbackEvents';
import { emitCallbackEvents } from './utils/emitCallbackEvents';
import { handleErrorsWithMessageBus } from './utils/handleErrors';

export interface VaultedResponse {
  refId: string;
}

export type OnErrorResponse = {
  type: Extract<CallbackState, 'failed_load' | 'failed_submission'>;
} & CallbackFailure;

interface Props {
  customerId?: number;
  countryCode?: CountryCode;
  handleCallbackEvents: (state: CallbackState, reponse: CallbackResponse) => void;
  displayError: (errorToDisplay: string) => void;
  track: (errorMessage: string, errorCode?: string) => void;
}

const S = {
  Container: styled.div`
    position: relative;
  `,

  FormContainer: styled.div`
    min-height: 600px;

    /* 
      zuora thought it would be nice to inject some opinionated styles on the iframe element.
      I didn't agree!
    */
    .z_hppm_iframe {
      width: 100% !important;
      background-color: unset !important;
    }
  `,
  LoadingButton: styled(Button)`
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 162px;
    height: 40px;
    justify-content: center;
    border: 2px solid ${tokens.ALIAS_COLORS.ui_background};
    box-sizing: content-box;
    padding: 0;
  `,
};

export const ZuoraPaymentForm = memo(
  ({ customerId, countryCode, handleCallbackEvents, displayError, track }: Props): ReactElement => {
    const [loading, setLoading] = useState(true);
    const zuora = useZuoraLibrary();
    const parameters = useZuoraParameters({ countryCode, customerId });
    const { t } = useTranslationWithPrefix('checkout:payment-details');
    const genericError = t('error-generic');
    const [zuoraSuccess, setZuoraSuccess] = useState(false);

    useEffect(() => {
      if (!zuora || !parameters) {
        return undefined;
      }

      if (parameters instanceof Error) {
        track(parameters.message);
        setLoading(false);
        return displayError(genericError);
      }

      const handleErrors = handleErrorsWithMessageBus(
        zuora.sendErrorMessageToHpm,
        displayError,
        track,
      );

      const callback = (response: CallbackResponse) => {
        setZuoraSuccess(response.success);
        emitCallbackEvents(handleCallbackEvents)(response);
      };

      zuora.setEventHandler('onloadCallback', () => {
        setLoading(false);
      });

      zuora.renderWithErrorHandler(parameters, {}, callback, handleErrors);

      return undefined;
    }, [zuora, parameters, displayError, track, handleCallbackEvents, genericError]);

    return (
      <S.Container>
        {/* the loader is overlayed on top of the form, because zuora needs to be able to see #zuora_payment to render the page */}
        {loading && <CreditCardDetailsLoader />}
        <S.FormContainer id="zuora_payment" aria-label={t('form-aria-label')} aria-busy={loading} />
        {/* 👇🏼 displays a loading button on top of the zuora button. We need this when the zuora form is done submitting but we 
            have to do another async action on our end before moving on to the next step/ui. We need this hack because:
              - We don't have control over the loading state of the zuora button
              - We are also not able to use an external button, context: https://hotjar.slack.com/archives/C015WCG0DNE/p1675331429512029. 
        */}
        {zuoraSuccess && <S.LoadingButton isLoading>Loading</S.LoadingButton>}
      </S.Container>
    );
  },
  /**
   * we never want this component to re-render cause the iframe form will exceed its attempts limit and throw an error
   */
  () => true,
);
